import { useState } from 'react';
import type { ReactNode } from 'react';
import type {
  KycState,
  LinkedBank,
  WalletBalances,
  WalletTransaction,
} from '@tradekaro/shared';
import { BrokerPicker } from '../../components/BrokerPicker';
import { Button, Card } from '../../components/UI';
import { Icon } from '../../components/Icons';
import { KYC_STATE, LINKED_BANKS, WALLET_BALANCES, WALLET_TXNS } from './data';
import { useBreakpoint } from '../../lib/useBreakpoint';
import {
  deposit as apiDeposit,
  useWalletBalance,
  useWalletTxns,
  withdraw as apiWithdraw,
} from '../../lib/dataHooks';

type Filter = 'all' | 'in' | 'out' | 'trade' | 'div';

export const Wallet = () => {
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [showKyc, setShowKyc] = useState(true);
  const [showAddBank, setShowAddBank] = useState(false);

  // Which broker's cash. Defaults to everything; the picker hides itself while
  // there is only one account.
  const [broker, setBroker] = useState('all');
  const balanceQ = useWalletBalance(15_000, broker);
  const txnsQ = useWalletTxns(0, broker);
  const live = !!balanceQ.data;
  const b: WalletBalances = balanceQ.data
    ? {
        totalCash: balanceQ.data.totalCash,
        available: balanceQ.data.available,
        inOpenOrders: balanceQ.data.inOpenOrders,
        unsettled: balanceQ.data.unsettled,
        dividendsPocket: balanceQ.data.dividendsPocket,
      }
    : WALLET_BALANCES;
  const banks = LINKED_BANKS;
  const txns: WalletTransaction[] = txnsQ.data?.transactions
    ? txnsQ.data.transactions.map((t) => ({
        id: t.id,
        type: (t.type === 'ADJUSTMENT' ? 'FEE' : t.type) as WalletTransaction['type'],
        amount: Number(t.amount),
        status: 'completed',
        date: new Date(t.occurredAt).toLocaleDateString('en-PK', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        }),
        time: new Date(t.occurredAt).toLocaleTimeString('en-PK', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        ref: t.reference ?? t.id.slice(0, 8),
        method: t.method ?? undefined,
        counterparty: t.counterparty ?? undefined,
      }))
    : WALLET_TXNS;
  const kyc: KycState = live ? { ...KYC_STATE, status: 'verified' } : KYC_STATE;
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const mainCols = isMobile || isTablet ? '1fr' : '1.1fr 1.5fr';
  const pagePad = isMobile ? '20px 16px 60px' : '24px 32px 80px';

  const fmt = (n: number) => Math.abs(n).toLocaleString('en-PK', { maximumFractionDigits: 0 });
  const fmt2 = (n: number) =>
    Math.abs(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const filtered =
    filter === 'all'
      ? txns
      : txns.filter((t) => {
          if (filter === 'in') return t.amount > 0;
          if (filter === 'out') return t.amount < 0;
          if (filter === 'trade') return t.type === 'BUY' || t.type === 'SELL';
          if (filter === 'div') return t.type === 'DIVIDEND';
          return true;
        });

  const flow = txns.reduce(
    (acc, t) => {
      if (t.amount > 0) acc.in += t.amount;
      else acc.out += -t.amount;
      return acc;
    },
    { in: 0, out: 0 },
  );

  return (
    <div
      style={{ maxWidth: 1440, margin: '0 auto', padding: pagePad }}
      className="tk-fade-in"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'flex-end',
          justifyContent: 'space-between',
          marginBottom: 24,
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--tk-ink-3)',
              fontFamily: 'var(--tk-mono)',
              marginBottom: 6,
            }}
          >
            Wallet · Cash & transfers
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700, letterSpacing: '-0.025em' }}>
              Your money
            </h1>
            <BrokerPicker value={broker} onChange={setBroker} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" size="md" icon="arrowDown">
            Statement
          </Button>
          <Button
            variant="ghost"
            size="md"
            icon="withdraw"
            onClick={() => setShowWithdraw(true)}
          >
            Withdraw
          </Button>
          <Button
            variant="primary"
            size="md"
            icon="deposit"
            onClick={() => setShowDeposit(true)}
          >
            Deposit funds
          </Button>
        </div>
      </div>

      {showKyc && kyc.status === 'pending' && (
        <KycBanner kyc={kyc} onDismiss={() => setShowKyc(false)} />
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: mainCols,
          gap: 20,
          marginTop: showKyc ? 20 : 0,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <BalanceHero b={b} fmt={fmt} fmt2={fmt2} />
          <BalanceBreakdown b={b} fmt={fmt} />
          <QuickActions
            onDeposit={() => setShowDeposit(true)}
            onWithdraw={() => setShowWithdraw(true)}
          />
          <LinkedBanks banks={banks} onAdd={() => setShowAddBank(true)} />
        </div>

        <div>
          <TransactionsCard
            txns={filtered}
            filter={filter}
            onFilter={setFilter}
            flow={flow}
            fmt={fmt}
          />
        </div>
      </div>

      {showDeposit && (
        <DepositModal
          banks={banks}
          onClose={() => setShowDeposit(false)}
          onSubmit={async (amount, method) => {
            await apiDeposit({ amount, method });
            await Promise.all([balanceQ.refetch(), txnsQ.refetch()]);
            setShowDeposit(false);
          }}
        />
      )}
      {showWithdraw && (
        <WithdrawModal
          banks={banks}
          available={b.available}
          onClose={() => setShowWithdraw(false)}
          onSubmit={async (amount, method) => {
            await apiWithdraw({ amount, method });
            await Promise.all([balanceQ.refetch(), txnsQ.refetch()]);
            setShowWithdraw(false);
          }}
        />
      )}
      {showAddBank && <AddBankModal onClose={() => setShowAddBank(false)} />}
    </div>
  );
};

const STEP_LABELS: Record<string, string> = {
  cnic: 'CNIC',
  liveness: 'Selfie',
  address: 'Address',
  'bank-verification': 'Bank',
};

const KycBanner = ({ kyc, onDismiss }: { kyc: KycState; onDismiss: () => void }) => {
  const total = kyc.completedSteps.length + kyc.pendingSteps.length;
  const done = kyc.completedSteps.length;
  return (
    <div
      style={{
        background:
          'linear-gradient(135deg, color-mix(in oklch, var(--tk-warn) 14%, var(--tk-surface)), var(--tk-surface))',
        border: '1px solid color-mix(in oklch, var(--tk-warn) 40%, var(--tk-line))',
        borderRadius: 'var(--tk-radius-lg)',
        padding: '18px 22px',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        gap: 18,
        alignItems: 'center',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: 'color-mix(in oklch, var(--tk-warn) 22%, transparent)',
          color: 'var(--tk-warn)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </svg>
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tk-ink)', marginBottom: 3 }}>
          Bank verification pending — withdrawals are limited until KYC is complete
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--tk-ink-2)' }}>
          Step {done + 1} of {total}: We&apos;re verifying your IBAN with State Bank. Estimated time:{' '}
          <strong>{kyc.estCompletion}</strong>.
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
          {kyc.completedSteps.map((s) => (
            <KycStep key={s} label={STEP_LABELS[s]} state="done" />
          ))}
          {kyc.pendingSteps.map((s, i) => (
            <KycStep key={s} label={STEP_LABELS[s]} state={i === 0 ? 'active' : 'pending'} />
          ))}
        </div>
      </div>
      <Button variant="primary" size="sm">
        Track status
      </Button>
      <button
        onClick={onDismiss}
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          border: 'none',
          background: 'transparent',
          color: 'var(--tk-ink-3)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

const KycStep = ({ label, state }: { label: string; state: 'done' | 'active' | 'pending' }) => {
  const tones = {
    done: { bg: 'var(--tk-up-soft)', fg: 'var(--tk-up)', dot: 'var(--tk-up)' },
    active: {
      bg: 'color-mix(in oklch, var(--tk-warn) 18%, transparent)',
      fg: 'var(--tk-warn)',
      dot: 'var(--tk-warn)',
    },
    pending: { bg: 'transparent', fg: 'var(--tk-ink-3)', dot: 'var(--tk-line)' },
  };
  const t = tones[state];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: 'var(--tk-mono)',
        padding: '4px 9px',
        borderRadius: 999,
        background: t.bg,
        color: t.fg,
        border: state === 'pending' ? '1px dashed var(--tk-line)' : 'none',
      }}
    >
      {state === 'active' ? (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 3,
            background: t.dot,
            animation: 'tk-pulse 1.6s var(--tk-ease) infinite',
          }}
        />
      ) : state === 'done' ? (
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M5 12l5 5L20 7" />
        </svg>
      ) : (
        <span style={{ width: 6, height: 6, borderRadius: 3, background: t.dot }} />
      )}
      {label}
    </span>
  );
};

const BalanceHero = ({
  b,
  fmt,
  fmt2,
}: {
  b: WalletBalances;
  fmt: (n: number) => string;
  fmt2: (n: number) => string;
}) => (
  <Card
    padding={24}
    style={{
      background: 'linear-gradient(135deg, var(--tk-accent), oklch(0.58 0.16 22))',
      color: 'var(--tk-accent-ink)',
      border: 'none',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            opacity: 0.78,
            fontFamily: 'var(--tk-mono)',
          }}
        >
          Total cash balance
        </div>
        <div
          className="mono"
          style={{
            fontSize: 40,
            fontWeight: 700,
            letterSpacing: '-0.028em',
            marginTop: 8,
            lineHeight: 1,
          }}
        >
          <span style={{ fontSize: 18, opacity: 0.7, marginRight: 8, fontWeight: 600 }}>PKR</span>
          {fmt2(b.totalCash)}
        </div>
        <div
          style={{
            fontSize: 12,
            marginTop: 8,
            opacity: 0.78,
            fontFamily: 'var(--tk-mono)',
          }}
        >
          Settled in 1 currency · Updated just now
        </div>
      </div>
      <span
        style={{
          padding: '5px 10px',
          background: 'color-mix(in oklch, white 18%, transparent)',
          color: 'white',
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          borderRadius: 999,
          fontFamily: 'var(--tk-mono)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 3,
            background: 'white',
            animation: 'tk-pulse 1.6s var(--tk-ease) infinite',
          }}
        />
        Live
      </span>
    </div>

    <div
      style={{
        marginTop: 22,
        paddingTop: 18,
        borderTop: '1px solid color-mix(in oklch, white 22%, transparent)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.85 }}>
        <span>Available to trade or withdraw</span>
        <span className="mono" style={{ fontWeight: 700 }}>
          PKR {fmt(b.available)}
        </span>
      </div>
      <div
        style={{
          marginTop: 8,
          height: 6,
          borderRadius: 3,
          background: 'color-mix(in oklch, white 18%, transparent)',
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        <div style={{ width: `${(b.available / b.totalCash) * 100}%`, background: 'white' }} />
        <div
          style={{
            width: `${(b.inOpenOrders / b.totalCash) * 100}%`,
            background: 'color-mix(in oklch, white 60%, transparent)',
          }}
        />
        <div
          style={{
            width: `${(b.unsettled / b.totalCash) * 100}%`,
            background: 'color-mix(in oklch, white 38%, transparent)',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginTop: 8,
          fontSize: 10.5,
          fontFamily: 'var(--tk-mono)',
          opacity: 0.85,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 6, height: 6, borderRadius: 1, background: 'white' }} /> Available
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 1,
              background: 'color-mix(in oklch, white 60%, transparent)',
            }}
          />{' '}
          In orders
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 1,
              background: 'color-mix(in oklch, white 38%, transparent)',
            }}
          />{' '}
          Unsettled
        </span>
      </div>
    </div>
  </Card>
);

const BalanceBreakdown = ({ b, fmt }: { b: WalletBalances; fmt: (n: number) => string }) => {
  const rows = [
    {
      label: 'In open orders',
      sub: 'Limit orders pending fill',
      value: b.inOpenOrders,
      tone: 'ink-2',
    },
    { label: 'Unsettled (T+2)', sub: 'From recent sells', value: b.unsettled, tone: 'ink-2' },
    {
      label: 'Dividend pocket',
      sub: 'Cash dividends received',
      value: b.dividendsPocket,
      tone: 'up',
    },
  ];
  return (
    <Card padding={0}>
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--tk-line-2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 600 }}>Where your cash sits</h3>
        <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', fontFamily: 'var(--tk-mono)' }}>
          3 buckets
        </span>
      </div>
      {rows.map((r, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '13px 20px',
            borderBottom: i < rows.length - 1 ? '1px solid var(--tk-line-2)' : 'none',
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tk-ink)' }}>{r.label}</div>
            <div style={{ fontSize: 11.5, color: 'var(--tk-ink-3)', marginTop: 2 }}>{r.sub}</div>
          </div>
          <span
            className="mono"
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: r.tone === 'up' ? 'var(--tk-up)' : 'var(--tk-ink)',
            }}
          >
            PKR {fmt(r.value)}
          </span>
        </div>
      ))}
    </Card>
  );
};

const QuickActions = ({
  onDeposit,
  onWithdraw,
}: {
  onDeposit: () => void;
  onWithdraw: () => void;
}) => {
  const actions = [
    {
      id: 'dep',
      label: 'Deposit',
      sub: 'Raast / Bank',
      icon: 'deposit' as const,
      onClick: onDeposit,
      primary: true,
    },
    {
      id: 'wd',
      label: 'Withdraw',
      sub: 'To linked bank',
      icon: 'withdraw' as const,
      onClick: onWithdraw,
    },
    {
      id: 'tx',
      label: 'Transfer',
      sub: 'Move to family',
      icon: 'transfer' as const,
    },
    {
      id: 'plus',
      label: 'Auto-invest',
      sub: 'Schedule SIP',
      icon: 'plus' as const,
    },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      {actions.map((a) => (
        <button
          key={a.id}
          onClick={a.onClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 16px',
            background: a.primary ? 'var(--tk-ink)' : 'var(--tk-surface)',
            color: a.primary ? 'var(--tk-bg)' : 'var(--tk-ink)',
            border: a.primary ? 'none' : '1px solid var(--tk-line)',
            borderRadius: 'var(--tk-radius-md)',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'all 0.18s var(--tk-ease)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
        >
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: a.primary
                ? 'color-mix(in oklch, white 14%, transparent)'
                : 'var(--tk-surface-2)',
              color: a.primary ? 'var(--tk-bg)' : 'var(--tk-ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name={a.icon} size={18} />
          </span>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>{a.label}</div>
            <div
              style={{
                fontSize: 11,
                opacity: a.primary ? 0.75 : 1,
                color: a.primary ? 'inherit' : 'var(--tk-ink-3)',
                marginTop: 1,
              }}
            >
              {a.sub}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

const LinkedBanks = ({
  banks,
  onAdd,
}: {
  banks: LinkedBank[];
  onAdd: () => void;
}) => (
  <Card padding={0}>
    <div
      style={{
        padding: '14px 20px',
        borderBottom: '1px solid var(--tk-line-2)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 600 }}>Linked banks</h3>
      <button
        onClick={onAdd}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--tk-accent)',
          fontSize: 12.5,
          fontWeight: 700,
          fontFamily: 'var(--tk-sans)',
        }}
      >
        <Icon name="plus" size={13} stroke={2.4} /> Add account
      </button>
    </div>
    {banks.map((bk, i) => (
      <div
        key={bk.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 20px',
          borderBottom: i < banks.length - 1 ? '1px solid var(--tk-line-2)' : 'none',
        }}
      >
        <BankAvatar bank={bk.bank} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--tk-ink)' }}>
              {bk.bank}
            </span>
            {bk.primary && <Pill tone="accent">Primary</Pill>}
            {!bk.verified && <Pill tone="warn">Verifying</Pill>}
          </div>
          <div
            className="mono"
            style={{
              fontSize: 11.5,
              color: 'var(--tk-ink-3)',
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {bk.iban}
          </div>
        </div>
        <button
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--tk-ink-3)',
            padding: 6,
            borderRadius: 6,
            display: 'flex',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="1.6" />
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="19" cy="12" r="1.6" />
          </svg>
        </button>
      </div>
    ))}
  </Card>
);

const BankAvatar = ({ bank }: { bank: string }) => {
  const initials = bank
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
  let h = 0;
  for (let i = 0; i < bank.length; i++) h = (h * 31 + bank.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return (
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: `oklch(0.92 0.06 ${hue})`,
        color: `oklch(0.4 0.12 ${hue})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 800,
        fontFamily: 'var(--tk-mono)',
        letterSpacing: '-0.02em',
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
};

type PillTone = 'accent' | 'warn' | 'up' | 'down' | 'neutral';

const Pill = ({ children, tone = 'neutral' }: { children: ReactNode; tone?: PillTone }) => {
  const tones: Record<PillTone, { bg: string; fg: string }> = {
    accent: { bg: 'var(--tk-accent-soft)', fg: 'var(--tk-accent)' },
    warn: {
      bg: 'color-mix(in oklch, var(--tk-warn) 18%, transparent)',
      fg: 'var(--tk-warn)',
    },
    up: { bg: 'var(--tk-up-soft)', fg: 'var(--tk-up)' },
    down: { bg: 'var(--tk-down-soft)', fg: 'var(--tk-down)' },
    neutral: { bg: 'var(--tk-surface-2)', fg: 'var(--tk-ink-2)' },
  };
  const t = tones[tone];
  return (
    <span
      style={{
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontFamily: 'var(--tk-mono)',
        padding: '2px 6px',
        borderRadius: 4,
        background: t.bg,
        color: t.fg,
      }}
    >
      {children}
    </span>
  );
};

interface TxnsCardProps {
  txns: WalletTransaction[];
  filter: Filter;
  onFilter: (f: Filter) => void;
  flow: { in: number; out: number };
  fmt: (n: number) => string;
}

const TransactionsCard = ({ txns, filter, onFilter, flow, fmt }: TxnsCardProps) => {
  const tabs: { id: Filter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'in', label: 'Money in' },
    { id: 'out', label: 'Money out' },
    { id: 'trade', label: 'Trades' },
    { id: 'div', label: 'Dividends' },
  ];

  const groups: Record<string, WalletTransaction[]> = {};
  txns.forEach((t) => {
    if (!groups[t.date]) groups[t.date] = [];
    groups[t.date].push(t);
  });

  return (
    <Card padding={0}>
      <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--tk-line-2)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 16,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Transaction history</h3>
            <div style={{ fontSize: 12, color: 'var(--tk-ink-3)', marginTop: 4 }}>
              Last 30 days · All money in and out of your wallet
            </div>
          </div>
          <Button variant="ghost" size="sm" icon="arrowDown">
            CSV
          </Button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 14,
            marginBottom: 14,
          }}
        >
          <FlowStat label="In" value={fmt(flow.in)} tone="up" />
          <FlowStat label="Out" value={fmt(flow.out)} tone="down" />
          <FlowStat
            label="Net"
            value={fmt(flow.in - flow.out)}
            tone={flow.in - flow.out >= 0 ? 'up' : 'down'}
            sign
          />
        </div>

        <div style={{ display: 'flex', gap: 4, overflowX: 'auto' }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => onFilter(t.id)}
              style={{
                padding: '7px 12px',
                fontSize: 12.5,
                fontWeight: 600,
                background: filter === t.id ? 'var(--tk-ink)' : 'transparent',
                color: filter === t.id ? 'var(--tk-bg)' : 'var(--tk-ink-2)',
                border: filter === t.id ? 'none' : '1px solid var(--tk-line)',
                borderRadius: 999,
                cursor: 'pointer',
                transition: 'all 0.16s var(--tk-ease)',
                whiteSpace: 'nowrap',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxHeight: 720, overflowY: 'auto' }}>
        {Object.keys(groups).length === 0 ? (
          <div
            style={{ padding: 60, textAlign: 'center', color: 'var(--tk-ink-3)', fontSize: 13 }}
          >
            No transactions match this filter.
          </div>
        ) : (
          Object.entries(groups).map(([date, rows]) => (
            <div key={date}>
              <div
                style={{
                  padding: '10px 22px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--tk-ink-3)',
                  fontFamily: 'var(--tk-mono)',
                  background: 'var(--tk-surface-2)',
                  borderBottom: '1px solid var(--tk-line-2)',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                {date}
              </div>
              {rows.map((t) => (
                <TxnRow key={t.id} t={t} fmt={fmt} />
              ))}
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

const FlowStat = ({
  label,
  value,
  tone,
  sign,
}: {
  label: string;
  value: string;
  tone: 'up' | 'down';
  sign?: boolean;
}) => (
  <div
    style={{
      padding: '10px 12px',
      background: 'var(--tk-surface-2)',
      borderRadius: 'var(--tk-radius-sm)',
    }}
  >
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--tk-ink-3)',
        fontFamily: 'var(--tk-mono)',
      }}
    >
      {label} <span style={{ fontWeight: 500 }}>30d</span>
    </div>
    <div
      className="mono"
      style={{
        fontSize: 17,
        fontWeight: 700,
        marginTop: 4,
        color: tone === 'up' ? 'var(--tk-up)' : 'var(--tk-down)',
        letterSpacing: '-0.02em',
      }}
    >
      {sign ? (tone === 'up' ? '+' : '−') : tone === 'up' ? '+' : '−'}
      {value}
    </div>
  </div>
);

interface TxnMeta {
  title: string;
  subtitle: string;
  icon: 'deposit' | 'withdraw' | 'arrowUp' | 'arrowDown' | 'star' | 'sliders' | 'transfer';
  iconBg: string;
  iconFg: string;
  aux?: string;
}

const txnMeta = (t: WalletTransaction): TxnMeta => {
  switch (t.type) {
    case 'DEPOSIT':
      return {
        title: `Deposit via ${t.method}`,
        subtitle: t.counterparty || '',
        icon: 'deposit',
        iconBg: 'var(--tk-up-soft)',
        iconFg: 'var(--tk-up)',
      };
    case 'WITHDRAW':
      return {
        title: `Withdrawal · ${t.method}`,
        subtitle: t.counterparty || '',
        icon: 'withdraw',
        iconBg: 'color-mix(in oklch, var(--tk-warn) 16%, transparent)',
        iconFg: 'var(--tk-warn)',
      };
    case 'BUY':
      return {
        title: `Buy ${t.sym}`,
        subtitle: `${t.qty} sh @ ${t.price?.toFixed(2)}`,
        icon: 'arrowDown',
        iconBg: 'var(--tk-surface-2)',
        iconFg: 'var(--tk-ink-2)',
        aux: `${t.qty} shares`,
      };
    case 'SELL':
      return {
        title: `Sell ${t.sym}`,
        subtitle: `${t.qty} sh @ ${t.price?.toFixed(2)}`,
        icon: 'arrowUp',
        iconBg: 'var(--tk-surface-2)',
        iconFg: 'var(--tk-ink-2)',
        aux: `${t.qty} shares`,
      };
    case 'DIVIDEND':
      return {
        title: `${t.sym} dividend`,
        subtitle: `${t.qty} sh @ PKR ${t.perShare?.toFixed(2)}/sh`,
        icon: 'star',
        iconBg: 'var(--tk-accent-soft)',
        iconFg: 'var(--tk-accent)',
      };
    case 'FEE':
      return {
        title: t.label || 'Fee',
        subtitle: 'Platform fee',
        icon: 'sliders',
        iconBg: 'var(--tk-surface-2)',
        iconFg: 'var(--tk-ink-3)',
      };
    default:
      return {
        title: t.type,
        subtitle: '',
        icon: 'transfer',
        iconBg: 'var(--tk-surface-2)',
        iconFg: 'var(--tk-ink-3)',
      };
  }
};

const TxnRow = ({ t, fmt }: { t: WalletTransaction; fmt: (n: number) => string }) => {
  const meta = txnMeta(t);
  const isIn = t.amount > 0;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '36px 1fr auto',
        gap: 14,
        alignItems: 'center',
        padding: '14px 22px',
        borderBottom: '1px solid var(--tk-line-2)',
        transition: 'background 0.15s var(--tk-ease)',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--tk-surface-2)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: meta.iconBg,
          color: meta.iconFg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={meta.icon} size={16} />
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tk-ink)' }}>
            {meta.title}
          </span>
          {t.status === 'settled' && <Pill tone="up">Settled</Pill>}
          {t.status === 'pending' && <Pill tone="warn">Pending</Pill>}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: 'var(--tk-ink-3)',
            marginTop: 2,
            fontFamily: 'var(--tk-mono)',
            display: 'flex',
            gap: 10,
          }}
        >
          <span>{t.time}</span>
          <span style={{ color: 'var(--tk-line)' }}>·</span>
          <span>{meta.subtitle}</span>
          <span style={{ color: 'var(--tk-line)' }}>·</span>
          <span style={{ opacity: 0.75 }}>{t.ref}</span>
        </div>
      </div>

      <div style={{ textAlign: 'right' }}>
        <div
          className="mono"
          style={{
            fontSize: 14.5,
            fontWeight: 700,
            color: isIn ? 'var(--tk-up)' : 'var(--tk-ink)',
          }}
        >
          {isIn ? '+' : '−'}PKR {fmt(t.amount)}
        </div>
        {meta.aux && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--tk-ink-3)',
              fontFamily: 'var(--tk-mono)',
              marginTop: 2,
            }}
          >
            {meta.aux}
          </div>
        )}
      </div>
    </div>
  );
};

const Modal = ({
  children,
  onClose,
  title,
  subtitle,
  width = 480,
}: {
  children: ReactNode;
  onClose: () => void;
  title: string;
  subtitle?: string;
  width?: number;
}) => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 100,
      background: 'color-mix(in oklch, black 50%, transparent)',
      backdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      animation: 'tk-fade-in 0.2s var(--tk-ease)',
    }}
    onClick={onClose}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%',
        maxWidth: width,
        background: 'var(--tk-surface)',
        borderRadius: 'var(--tk-radius-lg)',
        boxShadow: 'var(--tk-shadow-lg)',
        maxHeight: '90vh',
        overflow: 'auto',
      }}
    >
      <div
        style={{
          padding: '22px 24px 16px',
          borderBottom: '1px solid var(--tk-line-2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>
            {title}
          </h3>
          {subtitle && (
            <div style={{ fontSize: 12.5, color: 'var(--tk-ink-3)', marginTop: 4 }}>{subtitle}</div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: 'none',
            background: 'var(--tk-surface-2)',
            color: 'var(--tk-ink-2)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      {children}
    </div>
  </div>
);

const DepositModal = ({
  banks,
  onClose,
  onSubmit,
}: {
  banks: LinkedBank[];
  onClose: () => void;
  onSubmit: (amount: number, method: 'Raast' | 'IBAN') => Promise<void>;
}) => {
  const [method, setMethod] = useState<'raast' | 'iban'>('raast');
  const [amount, setAmount] = useState('25,000');
  const [bank, setBank] = useState(banks.find((b) => b.primary)?.id || banks[0].id);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const presets = [10000, 25000, 50000, 100000];

  const handleSubmit = async () => {
    const numeric = Number(amount.replace(/[^\d.]/g, ''));
    if (!numeric || numeric <= 0) {
      setError('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(numeric, method === 'raast' ? 'Raast' : 'IBAN');
    } catch (e) {
      setError((e as Error).message || 'Deposit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      title="Deposit funds"
      subtitle="Add cash from your bank to your TradeKaro wallet"
      width={520}
    >
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 22 }}>
          <Label>Method</Label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
            <MethodCard
              active={method === 'raast'}
              onClick={() => setMethod('raast')}
              title="Raast"
              subtitle="Instant · Free"
              badge="Recommended"
              icon={<RaastGlyph />}
            />
            <MethodCard
              active={method === 'iban'}
              onClick={() => setMethod('iban')}
              title="IBAN transfer"
              subtitle="1–2 hours · Free"
              icon={<Icon name="transfer" size={20} />}
            />
          </div>
        </div>

        <div style={{ marginBottom: 22 }}>
          <Label>From bank account</Label>
          <select
            value={bank}
            onChange={(e) => setBank(e.target.value)}
            style={{
              width: '100%',
              marginTop: 8,
              padding: '12px 14px',
              background: 'var(--tk-surface-2)',
              border: '1px solid var(--tk-line)',
              borderRadius: 'var(--tk-radius-sm)',
              fontSize: 14,
              fontFamily: 'var(--tk-sans)',
              color: 'var(--tk-ink)',
              appearance: 'none',
            }}
          >
            {banks
              .filter((b) => b.verified)
              .map((b) => (
                <option key={b.id} value={b.id}>
                  {b.bank} · {b.iban.slice(-7)}
                </option>
              ))}
          </select>
        </div>

        <div style={{ marginBottom: 22 }}>
          <Label>Amount</Label>
          <div
            style={{
              marginTop: 8,
              position: 'relative',
              background: 'var(--tk-surface-2)',
              border: '2px solid var(--tk-accent)',
              borderRadius: 'var(--tk-radius-md)',
              padding: '18px 18px',
              display: 'flex',
              alignItems: 'baseline',
            }}
          >
            <span
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: 'var(--tk-ink-3)',
                fontFamily: 'var(--tk-mono)',
                marginRight: 10,
              }}
            >
              PKR
            </span>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                background: 'transparent',
                outline: 'none',
                fontSize: 30,
                fontWeight: 700,
                fontFamily: 'var(--tk-mono)',
                color: 'var(--tk-ink)',
                letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums',
                minWidth: 0,
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {presets.map((p) => (
              <button
                key={p}
                onClick={() => setAmount(p.toLocaleString('en-PK'))}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'var(--tk-mono)',
                  background: 'var(--tk-surface-2)',
                  border: '1px solid var(--tk-line)',
                  borderRadius: 'var(--tk-radius-sm)',
                  color: 'var(--tk-ink-2)',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {p / 1000 + 'K'}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            padding: '14px 16px',
            background: 'var(--tk-surface-2)',
            borderRadius: 'var(--tk-radius-sm)',
            fontSize: 12.5,
            marginBottom: 20,
          }}
        >
          <SummaryRow label="Amount" value={`PKR ${amount}`} />
          <SummaryRow label="Fee" value="Free" tone="up" />
          <SummaryRow label="Arrives" value={method === 'raast' ? 'Instantly' : 'In 1–2 hours'} bold />
        </div>

        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--tk-radius-sm)',
              background: 'var(--tk-down-soft)',
              color: 'var(--tk-down)',
              fontSize: 12.5,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          style={{
            width: '100%',
            justifyContent: 'center',
            opacity: submitting ? 0.6 : 1,
            pointerEvents: submitting ? 'none' : 'auto',
          }}
        >
          {submitting
            ? 'Processing…'
            : method === 'raast'
              ? 'Confirm via Raast'
              : 'Confirm IBAN deposit'}
        </Button>
        <div
          style={{
            fontSize: 11,
            color: 'var(--tk-ink-3)',
            textAlign: 'center',
            marginTop: 12,
          }}
        >
          🔒 Funds are held in a segregated SECP-monitored account
        </div>
      </div>
    </Modal>
  );
};

const WithdrawModal = ({
  banks,
  available,
  onClose,
  onSubmit,
}: {
  banks: LinkedBank[];
  available: number;
  onClose: () => void;
  onSubmit: (amount: number, method: 'IBFT') => Promise<void>;
}) => {
  const [amount, setAmount] = useState('15,000');
  const [bank, setBank] = useState(banks.find((b) => b.primary)?.id || banks[0].id);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fmt = (n: number) => n.toLocaleString('en-PK', { maximumFractionDigits: 0 });

  const handleSubmit = async () => {
    const numeric = Number(amount.replace(/[^\d.]/g, ''));
    if (!numeric || numeric <= 0) {
      setError('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(numeric, 'IBFT');
    } catch (e) {
      setError((e as Error).message || 'Withdrawal failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      title="Withdraw to bank"
      subtitle="Send cash from your wallet to your linked bank account"
      width={520}
    >
      <div style={{ padding: 24 }}>
        <div
          style={{
            padding: '14px 16px',
            background: 'var(--tk-surface-2)',
            borderRadius: 'var(--tk-radius-sm)',
            marginBottom: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 12.5, color: 'var(--tk-ink-2)' }}>Available to withdraw</span>
          <span className="mono" style={{ fontSize: 18, fontWeight: 700 }}>
            PKR {fmt(available)}
          </span>
        </div>

        <div style={{ marginBottom: 22 }}>
          <Label>To bank account</Label>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {banks
              .filter((b) => b.verified)
              .map((b) => (
                <label
                  key={b.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    border:
                      bank === b.id
                        ? '2px solid var(--tk-accent)'
                        : '1px solid var(--tk-line)',
                    background: bank === b.id ? 'var(--tk-accent-soft)' : 'var(--tk-surface)',
                    borderRadius: 'var(--tk-radius-sm)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="wd-bank"
                    checked={bank === b.id}
                    onChange={() => setBank(b.id)}
                    style={{ accentColor: 'var(--tk-accent)' }}
                  />
                  <BankAvatar bank={b.bank} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{b.bank}</div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--tk-ink-3)' }}>
                      {b.iban}
                    </div>
                  </div>
                  {b.primary && <Pill tone="accent">Primary</Pill>}
                </label>
              ))}
          </div>
        </div>

        <div style={{ marginBottom: 22 }}>
          <Label>Amount</Label>
          <div
            style={{
              marginTop: 8,
              background: 'var(--tk-surface-2)',
              border: '2px solid var(--tk-accent)',
              borderRadius: 'var(--tk-radius-md)',
              padding: 18,
              display: 'flex',
              alignItems: 'baseline',
            }}
          >
            <span
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: 'var(--tk-ink-3)',
                fontFamily: 'var(--tk-mono)',
                marginRight: 10,
              }}
            >
              PKR
            </span>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                background: 'transparent',
                outline: 'none',
                fontSize: 30,
                fontWeight: 700,
                fontFamily: 'var(--tk-mono)',
                color: 'var(--tk-ink)',
                letterSpacing: '-0.02em',
                minWidth: 0,
              }}
            />
            <button
              onClick={() => setAmount(fmt(available))}
              style={{
                padding: '6px 10px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontFamily: 'var(--tk-mono)',
                background: 'var(--tk-accent)',
                color: 'var(--tk-accent-ink)',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Max
            </button>
          </div>
        </div>

        <div
          style={{
            padding: '14px 16px',
            background: 'var(--tk-surface-2)',
            borderRadius: 'var(--tk-radius-sm)',
            fontSize: 12.5,
            marginBottom: 20,
          }}
        >
          <SummaryRow label="Withdrawing" value={`PKR ${amount}`} />
          <SummaryRow label="Fee" value="Free" tone="up" />
          <SummaryRow label="Arrives" value="Within 24 hours · IBFT" bold />
        </div>

        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--tk-radius-sm)',
              background: 'var(--tk-down-soft)',
              color: 'var(--tk-down)',
              fontSize: 12.5,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          style={{
            width: '100%',
            justifyContent: 'center',
            opacity: submitting ? 0.6 : 1,
            pointerEvents: submitting ? 'none' : 'auto',
          }}
        >
          {submitting ? 'Processing…' : 'Confirm withdrawal'}
        </Button>
      </div>
    </Modal>
  );
};

const AddBankModal = ({ onClose }: { onClose: () => void }) => {
  const [iban, setIban] = useState('PK');
  return (
    <Modal
      onClose={onClose}
      title="Add a bank account"
      subtitle="Link a Pakistani bank account to deposit and withdraw"
      width={480}
    >
      <div style={{ padding: 24 }}>
        <Label>IBAN</Label>
        <input
          value={iban}
          onChange={(e) => setIban(e.target.value.toUpperCase())}
          placeholder="PK36 MCB0 0000 0000 0000 0000"
          style={{
            width: '100%',
            marginTop: 8,
            padding: '14px 16px',
            background: 'var(--tk-surface-2)',
            border: '1px solid var(--tk-line)',
            borderRadius: 'var(--tk-radius-sm)',
            fontSize: 16,
            fontFamily: 'var(--tk-mono)',
            letterSpacing: '0.02em',
            color: 'var(--tk-ink)',
            outline: 'none',
          }}
        />
        <div style={{ fontSize: 11.5, color: 'var(--tk-ink-3)', marginTop: 8 }}>
          We&apos;ll send a small verification deposit (PKR 1) — confirm the amount within 24 hours
          to activate.
        </div>

        <div
          style={{
            marginTop: 20,
            padding: '14px 16px',
            background: 'var(--tk-surface-2)',
            borderRadius: 'var(--tk-radius-sm)',
            fontSize: 12,
            color: 'var(--tk-ink-2)',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <Icon name="bell" size={16} />
          <span>
            The account title must match your CNIC. Joint accounts require both holders to sign in
            TradeKaro.
          </span>
        </div>

        <Button
          variant="primary"
          size="lg"
          style={{ width: '100%', justifyContent: 'center', marginTop: 20 }}
        >
          Continue
        </Button>
      </div>
    </Modal>
  );
};

const Label = ({ children }: { children: ReactNode }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--tk-ink-3)',
      fontFamily: 'var(--tk-mono)',
    }}
  >
    {children}
  </span>
);

const SummaryRow = ({
  label,
  value,
  tone,
  bold,
}: {
  label: string;
  value: string;
  tone?: 'up';
  bold?: boolean;
}) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
    <span style={{ color: 'var(--tk-ink-2)' }}>{label}</span>
    <span
      className="mono"
      style={{
        fontWeight: bold ? 700 : 500,
        color: tone === 'up' ? 'var(--tk-up)' : 'var(--tk-ink)',
      }}
    >
      {value}
    </span>
  </div>
);

const MethodCard = ({
  active,
  onClick,
  title,
  subtitle,
  badge,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
  badge?: string;
  icon: ReactNode;
}) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 6,
      padding: '14px 14px',
      background: active ? 'var(--tk-accent-soft)' : 'var(--tk-surface)',
      border: active ? '2px solid var(--tk-accent)' : '1px solid var(--tk-line)',
      borderRadius: 'var(--tk-radius-md)',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'all 0.15s',
      position: 'relative',
    }}
  >
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ color: active ? 'var(--tk-accent)' : 'var(--tk-ink-2)' }}>{icon}</div>
      {badge && <Pill tone="accent">{badge}</Pill>}
    </div>
    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tk-ink)' }}>{title}</div>
    <div style={{ fontSize: 11.5, color: 'var(--tk-ink-3)' }}>{subtitle}</div>
  </button>
);

const RaastGlyph = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12h6l2-4 2 8 2-4h6" />
    <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
  </svg>
);
